import React, { useEffect } from 'react';
import { 
  Grid, 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Typography,
  Box,
  Tooltip,
  IconButton,
  Autocomplete,
  Paper,
  FormControlLabel,
  Switch,
  FormHelperText,
} from '@mui/material';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { registrationService } from '../../services/registrationService';
import EventScheduleFields from './common/EventScheduleFields';
import { useBrand } from '../../brands/BrandProvider';

// Lista de estados brasileiros para autocompletar
const brazilianStates = [
  { value: 'AC', label: 'Acre' },
  { value: 'AL', label: 'Alagoas' },
  { value: 'AP', label: 'Amapá' },
  { value: 'AM', label: 'Amazonas' },
  { value: 'BA', label: 'Bahia' },
  { value: 'CE', label: 'Ceará' },
  { value: 'DF', label: 'Distrito Federal' },
  { value: 'ES', label: 'Espírito Santo' },
  { value: 'GO', label: 'Goiás' },
  { value: 'MA', label: 'Maranhão' },
  { value: 'MT', label: 'Mato Grosso' },
  { value: 'MS', label: 'Mato Grosso do Sul' },
  { value: 'MG', label: 'Minas Gerais' },
  { value: 'PA', label: 'Pará' },
  { value: 'PB', label: 'Paraíba' },
  { value: 'PR', label: 'Paraná' },
  { value: 'PE', label: 'Pernambuco' },
  { value: 'PI', label: 'Piauí' },
  { value: 'RJ', label: 'Rio de Janeiro' },
  { value: 'RN', label: 'Rio Grande do Norte' },
  { value: 'RS', label: 'Rio Grande do Sul' },
  { value: 'RO', label: 'Rondônia' },
  { value: 'RR', label: 'Roraima' },
  { value: 'SC', label: 'Santa Catarina' },
  { value: 'SP', label: 'São Paulo' },
  { value: 'SE', label: 'Sergipe' },
  { value: 'TO', label: 'Tocantins' }
];

interface BasicInfoFormProps {
  slugError: string | null;
  setSlugError: React.Dispatch<React.SetStateAction<string | null>>;
  checking: boolean;
  setChecking: React.Dispatch<React.SetStateAction<boolean>>;
}

const BasicInfoForm: React.FC<BasicInfoFormProps> = ({ slugError, setSlugError, checking, setChecking }) => {
  const { control, formState: { errors }, setValue } = useFormContext();
  const capacityValue = useWatch({ control, name: 'capacity' });
  const categoryValue = useWatch({ control, name: 'category' });
  const hasCapacity = Number(capacityValue) > 0;
  const brand = useBrand();
  const eventCategories = brand.copy.eventForm.categories;
  const copy = brand.copy.eventForm;

  useEffect(() => {
    const mapped = copy.categoryAliases?.[categoryValue];
    if (mapped && mapped !== categoryValue) {
      setValue('category', mapped);
    }
  }, [categoryValue, copy.categoryAliases, setValue]);
  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        Informações Básicas
        <Tooltip title="Preencha as informações essenciais do seu evento. Campos com * são obrigatórios.">
          <IconButton size="small" sx={{ ml: 1 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Typography>
      
      <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Título do Evento *"
                  placeholder={copy.titlePlaceholder}
                  error={!!errors.title}
                  helperText={errors.title?.message as string || 'Um título claro e atrativo para seu evento'}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
         <Grid item xs={12}>
          <Controller
            name="slug"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Identificador do Link *"
                placeholder={copy.slugPlaceholder}
                error={!!errors.slug || !!slugError}
                helperText={
                  errors.slug?.message as string ||
                  slugError ||
                  copy.slugHelper
                }
                InputLabelProps={{ shrink: true }}
                onBlur={async (e) => {
                  field.onBlur();
                  const value = field.value;
                  if (!value || value.length < 3) return;

                  try {
                    setChecking(true);
                    const { available } = await registrationService.checkSlugAvailability(value);
                    if (!available) {
                      setSlugError('Este identificador já está em uso.');
                    } else {
                      setSlugError(null);
                    }
                  } catch (err) {
                    setSlugError('Erro ao verificar disponibilidade.');
                  } finally {
                    setChecking(false);
                  }
                }}
              />
            )}
          />
        </Grid>
          
          <Grid item xs={12}>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={4}
                  label="Descrição *"
                  placeholder={copy.descriptionPlaceholder}
                  error={!!errors.description}
                  helperText={errors.description?.message as string || 'Uma boa descrição aumenta o interesse dos participantes'}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            <EventScheduleFields />
          </Grid>
          
          <Grid item xs={12}>
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Nome do Local *"
                  placeholder={copy.locationPlaceholder}
                  error={!!errors.location}
                  helperText={errors.location?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="mapLink"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Link do Mapa (Opcional)"
                  placeholder="Ex: https://maps.google.com/?q=..."
                  error={!!errors.mapLink}
                  helperText={errors.mapLink?.message as string || 'Cole aqui um link direto do Google Maps'}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12}>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Endereço *"
                  placeholder="Ex: Rua das Flores, 123"
                  error={!!errors.address}
                  helperText={errors.address?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  label="Cidade *"
                  error={!!errors.city}
                  helperText={errors.city?.message as string}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={brazilianStates}
                  getOptionLabel={(option) => option.label}
                  value={brazilianStates.find(state => state.value === field.value) || null}
                  onChange={(_, newValue) => {
                    field.onChange(newValue ? newValue.value : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Estado *"
                      error={!!errors.state}
                      helperText={errors.state?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              )}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.category}>
                  <InputLabel id="category-label">Categoria *</InputLabel>
                  <Select
                    {...field}
                    labelId="category-label"
                    label="Categoria *"
                  >
                    {eventCategories.map((category) => (
                      <MenuItem key={category.value} value={category.value}>
                        {category.label}
                      </MenuItem>
                    ))}
                    {field.value &&
                      !eventCategories.some((c) => c.value === field.value) && (
                        <MenuItem value={field.value}>{field.value}</MenuItem>
                      )}
                  </Select>
                  {errors.category && (
                    <Typography variant="caption" color="error">
                      {errors.category.message as string}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Controller
              name="capacity"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  label="Capacidade *"
                  placeholder="Número máximo de participantes"
                  InputProps={{ inputProps: { min: 1 } }}
                  error={!!errors.capacity}
                  helperText={errors.capacity?.message as string || 'Deixe em branco para capacidade ilimitada'}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="showRemainingSpots"
              control={control}
              defaultValue={false}
              render={({ field }) => (
                <FormControl fullWidth disabled={!hasCapacity}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!field.value && hasCapacity}
                        onChange={(e) => field.onChange(e.target.checked)}
                        disabled={!hasCapacity}
                      />
                    }
                    label="Mostrar vagas restantes na página pública"
                  />
                  <FormHelperText>
                    {hasCapacity
                      ? 'Exibe quantas vagas ainda restam (capacidade − inscritos pagos) no card do evento.'
                      : 'Defina uma capacidade maior que zero para habilitar esta opção.'}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
        </Grid>
      </Paper>
      
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Dica: Um título claro e uma descrição detalhada aumentam as chances de seu evento atrair mais participantes.
        </Typography>
      </Box>
    </Box>
  );
};

export default BasicInfoForm;
